import LoadingSpinner from '@/components/animations/LoadingSpinner/LoadingSpinner';
import { Button } from '@/components/elements/forms/buttons';
import PinInput, { UsePinInputResult } from '@/components/elements/forms/PinInput/PinInput';
import HR from '@/components/elements/HR/HR';
import { useCountDown } from '@/hooks/useCountDown';
import { LoginContextType } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

type VerifyOTPProps = UsePinInputResult & Pick<LoginContextType, 'email' | 'handleEmailResendClicked'>;

const translationKey = 'VerifyOTP';

let abortLoadingSpinner: boolean = false;

function getOneMinuteFromNowInMilliseconds(): number {
  return new Date().getTime() + 60 * 1000;
}

const VerifyOTP = ({
  email,
  pin,
  activePinIndex,
  error,
  clipError,
  submitting,
  pinContainerRef,
  inputRef,
  handleFocus,
  handlePaste,
  handleOnChange,
  handlePinChange,
  handleEmailResendClicked,
}: VerifyOTPProps) => {
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [verificationCodeCountdown, setVerificationCodeCountdown] = useState<boolean>(false);
  const location = useLocation();

  const endTimeRef = useRef<number | null>(null);

  function handleResentOTPCodeClick() {
    handleEmailResendClicked(location.pathname);
    endTimeRef.current = getOneMinuteFromNowInMilliseconds();
    setVerificationCodeCountdown(true);
  }

  function handleResentOTPResendCountdownComplete() {
    setVerificationCodeCountdown(false);
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (submitting) {
      abortLoadingSpinner = false;
      timeout = setTimeout(() => {
        if (abortLoadingSpinner) return;
        setShowLoadingSpinner(true);
      }, 300);
    } else {
      abortLoadingSpinner = true;
      timeout && clearTimeout(timeout);
      setShowLoadingSpinner(false);
    }

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [submitting]);

  return (
    <>
      <p className="text-fg_secondary pb-md text-m">
        {_s(`${translationKey}.checkInbox`)}
        <span className="text-fg_link">{email.value}</span>
      </p>
      {showLoadingSpinner && (
        <div className="py-lg relative flex flex-col items-center justify-center">
          <LoadingSpinner loop style={{ width: 80, height: 80 }} />
        </div>
      )}
      {!showLoadingSpinner && (
        <>
          <PinInput
            inputRef={inputRef}
            pinContainerRef={pinContainerRef}
            pin={pin}
            activePinIndex={activePinIndex}
            error={error}
            clipError={clipError}
            submitting={submitting}
            handleFocus={handleFocus}
            handlePaste={handlePaste}
            handlePinChange={handlePinChange}
            handleOnChange={handleOnChange}
          />
          <div className="space-y-md pt-md flex flex-col">
            {!error && !clipError && <span className="text-fg_secondary text-m">{_s(`${translationKey}.delay`)}</span>}
            <HR verticalSpacing="0" />
            <div className="flex items-center justify-between">
              {verificationCodeCountdown && (
                <Fragment>
                  <span className="text-fg_secondary text-m">{_s(`${translationKey}.resendIn`)}</span>
                  <OTPCountdownTimer endTime={endTimeRef.current} onComplete={handleResentOTPResendCountdownComplete} />
                </Fragment>
              )}
              {!verificationCodeCountdown && (
                <Fragment>
                  <span className="text-fg_secondary text-m">{_s(`${translationKey}.noEmail`)}</span>
                  <Button className="flex-shrink-0" variant="primary" onClick={handleResentOTPCodeClick}>
                    {_s(`${translationKey}.resend`)}
                  </Button>
                </Fragment>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const OTPCountdownTimer = memo(
  ({ endTime, onComplete }: { endTime: number; onComplete: () => void }) => {
    const countdown = useCountDown(endTime);
    const secondsLeft = countdown[3].value;

    useEffect(() => {
      if (secondsLeft <= 0) {
        onComplete();
      }
    }, [secondsLeft]);

    return secondsLeft >= 0 ? (
      <span className="text-m text-fg_primary font-semibold">
        00:{secondsLeft >= 10 ? secondsLeft : `0${secondsLeft}`}
      </span>
    ) : null;
  },
  () => true, // always return true to prevent re-renders
);
export default VerifyOTP;
